import { AlertTriangle, FileText, Eye, EyeOff, Shield } from 'lucide-react';
import React, { useState, useEffect } from 'react';

const EnhancedNotepadContent = ({ scenario, onComplete, fileContent }) => {
  const [content, setContent] = useState('');
  const [fileName, setFileName] = useState('Untitled');
  const [isModified, setIsModified] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);

  useEffect(() => {
    if (fileContent) {
      setContent(fileContent.content || '');
      setFileName(fileContent.name || 'Untitled');
      setIsModified(false);
      
      // Auto-analyze if it's a suspicious file
      if (fileContent.suspicious) {
        analyzeContent(fileContent.content || '');
      }
    }
  }, [fileContent]);

  const analyzeContent = (text) => {
    const analysis = {
      suspiciousPatterns: [],
      riskLevel: 'Low',
      recommendations: []
    };

    // Check for common malicious patterns
    if (text.includes('powershell -e') || text.includes('powershell -EncodedCommand')) {
      analysis.suspiciousPatterns.push('Base64-encoded PowerShell command detected');
      analysis.riskLevel = 'High';
      analysis.recommendations.push('This appears to be obfuscated PowerShell code. Do not execute.');
    }

    if (text.includes('Invoke-WebRequest') || text.includes('IWR')) {
      analysis.suspiciousPatterns.push('Web request command detected');
      analysis.riskLevel = 'Medium';
      analysis.recommendations.push('This script may download content from the internet.');
    }

    if (text.includes('Start-Process') || text.includes('StartProcess')) {
      analysis.suspiciousPatterns.push('Process execution command detected');
      analysis.riskLevel = 'Medium';
      analysis.recommendations.push('This script may execute other programs.');
    }

    if (text.includes('Remove-Item') || text.includes('del ') || text.includes('rmdir')) {
      analysis.suspiciousPatterns.push('File deletion command detected');
      analysis.riskLevel = 'High';
      analysis.recommendations.push('This script may delete files. Exercise extreme caution.');
    }

    if (text.includes('regsvr32') || text.includes('mshta.exe')) {
      analysis.suspiciousPatterns.push('Living Off the Land binary detected');
      analysis.riskLevel = 'High';
      analysis.recommendations.push('This uses legitimate Windows tools for potentially malicious purposes.');
    }

    if (text.includes('javascript:') || text.includes('vbscript:')) {
      analysis.suspiciousPatterns.push('Script execution detected');
      analysis.riskLevel = 'High';
      analysis.recommendations.push('This may execute JavaScript or VBScript code.');
    }

    // Check for obfuscation
    if (text.length > 100 && text.includes('JAB') && text.includes('==')) {
      analysis.suspiciousPatterns.push('Base64 obfuscation detected');
      analysis.riskLevel = 'High';
      analysis.recommendations.push('This appears to be heavily obfuscated code.');
    }

    setAnalysisResults(analysis);
    setShowAnalysis(true);
  };

  const handleContentChange = (e) => {
    setContent(e.target.value);
    setIsModified(true);
  };

  const handleQuarantine = () => {
    onComplete({
      success: true,
      message: 'Suspicious file quarantined.',
      correct: 1,
      total: 1,
      score: 100
    });
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'High': return 'text-red-600 bg-red-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="h-full flex flex-col bg-white text-black">
      {/* Title Bar */}
      <div className="flex items-center justify-between p-2 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-2">
          <FileText size={16} className="text-gray-600" />
          <span className="text-sm font-medium text-gray-700">
            {fileName}{isModified ? ' *' : ''}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowAnalysis(!showAnalysis)}
            className="p-1 hover:bg-gray-200 rounded"
            title="Toggle Analysis"
          >
            {showAnalysis ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col">
          <textarea
            value={content}
            onChange={handleContentChange}
            className="flex-1 p-4 border-none outline-none resize-none font-mono text-sm leading-relaxed"
            placeholder="Enter text here..."
            spellCheck={false}
          />
        </div>

        {/* Analysis Panel */}
        {showAnalysis && analysisResults && (
          <div className="w-80 border-l border-gray-200 p-4 bg-gray-50 overflow-y-auto">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
              <AlertTriangle size={16} className="mr-2" />
              Security Analysis
            </h3>

            {/* Risk Level */}
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Risk Level:</div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(analysisResults.riskLevel)}`}>
                {analysisResults.riskLevel} Risk
              </div>
            </div>

            {/* Suspicious Patterns */}
            {analysisResults.suspiciousPatterns.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Suspicious Patterns:</div>
                <div className="space-y-2">
                  {analysisResults.suspiciousPatterns.map((pattern, index) => (
                    <div key={index} className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                      <AlertTriangle size={14} className="inline mr-1" />
                      {pattern}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {analysisResults.recommendations.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Recommendations:</div>
                <div className="space-y-2">
                  {analysisResults.recommendations.map((rec, index) => (
                    <div key={index} className="p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                      <Shield size={14} className="inline mr-1" />
                      {rec}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {analysisResults.riskLevel === 'High' && (
              <div className="mt-6">
                <button
                  onClick={handleQuarantine}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center justify-center space-x-2"
                >
                  <Shield size={16} />
                  <span>Quarantine File</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="border-t border-gray-200 p-1 text-xs text-gray-500 flex justify-between">
        <span>Line 1, Column 1</span>
        <span>{content.length} characters</span>
        <span>{analysisResults ? `${analysisResults.riskLevel} Risk` : 'No Analysis'}</span>
      </div>
    </div>
  );
};

export default EnhancedNotepadContent;